import React, { useEffect, useLayoutEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Icon } from '@iq/react-components';

import { setActiveComponentId } from '../../../../../bundles/application';
import { useComponentPanel } from '../../ComponentPanelState';
import ComponentSelect from '../../../../ComponentSelect';
import TreeItem from './TreeItem';

const ComponentTree = () => {
  const dispatch = useDispatch();

  const {
    tree: componentTree,
    endingDepth,
    expanded: displayExpanded,
    flatList: flat,
    modelsHashmap,
    pageIndex,
    activeComponentId,
    pageComponents: shown,
    startingDepth,
    withToolbar: toolbar,
    createdComponent,
    showDeleteTreeButton,
    canDelete,
    rootComponent,
    setShowDeleteDialog,
    scrollToActive,
    enableAutoScroll,
  } = useComponentPanel();

  const expandedDepth = displayExpanded ? startingDepth + 1 : startingDepth;
  const findExpandedIds = useCallback(
    (node, depth = 1) => {
      if (!node) return shown?.map((c) => c.id) || [];
      const expandedIds = [];
      const xDepth = expandedDepth || Infinity;
      if (depth <= xDepth && node.children?.length) {
        expandedIds.push(node.id);
        for (const child of node.children) expandedIds.push(...findExpandedIds(child, depth + 1));
      }
      return expandedIds;
    },
    [expandedDepth, shown]
  );

  const shownIds = useMemo(() => shown?.map((c) => c.id) || [], [shown]);

  const [showFilters, setShowFilters] = useState(false);
  const [newComponent, setNewComponent] = useState({});
  const [expandedIdsByPage, setExpandedIdsByPage] = useState(
    flat ? {} : { [pageIndex]: findExpandedIds(componentTree) }
  );

  const filterPanel = useRef(null);
  const componentsReference = useRef({});
  const compsList = useRef(null);

  const onSearchSelect = (selection) => {
    dispatch(setActiveComponentId(selection));
  };

  const reset = useCallback(() => {
    dispatch(setActiveComponentId(null));
    setExpandedIdsByPage((previous) => ({
      ...previous,
      [pageIndex]: findExpandedIds(componentTree),
    }));
  }, [pageIndex, findExpandedIds, componentTree]);

  useEffect(() => {
    setExpandedIdsByPage((previous) => {
      if (!expandedIdsByPage[pageIndex]) {
        return { ...previous, [pageIndex]: findExpandedIds(componentTree) };
      }
      return previous;
    });
  }, [expandedIdsByPage, pageIndex, findExpandedIds, componentTree]);

  // Consolidated effect to handle both activeComponent and activeComponentId expansion
  useEffect(() => {
    if (activeComponentId) {
      const selected = shown?.find((comp) => comp.id === activeComponentId);
      const selectedIds = selected ? selected.ancestorIds : [];

      setExpandedIdsByPage((previous) => {
        const currentExpanded = previous[pageIndex] || [];
        const newIds = [activeComponentId, ...selectedIds].filter(
          (id) => !currentExpanded.includes(id)
        );

        if (newIds.length > 0) {
          return {
            ...previous,
            [pageIndex]: [...currentExpanded, ...newIds],
          };
        }
        return previous;
      });
    }
  }, [shown, activeComponentId, pageIndex]);

  useEffect(() => {
    setNewComponent(createdComponent);
  }, [createdComponent]);

  useLayoutEffect(() => {
    // Use a small timeout to ensure DOM is updated after expansion changes
    const scrollTimeout = setTimeout(() => {
      const item = componentsReference.current?.[activeComponentId];
      const list = compsList.current;
      if (item && list && expandedIdsByPage[pageIndex]?.includes(activeComponentId)) {
        const { top: indexT, bottom: indexB } = item.getBoundingClientRect();
        const { top: lT, bottom: lB } = list.getBoundingClientRect();
        const shouldScroll =
          (list.scrollHeight > list.clientHeight && (indexT < lT || indexB > lB + 30)) || false;
        if (shouldScroll && scrollToActive && activeComponentId) {
          item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          // Reset scroll state after scrolling happens
          setTimeout(() => enableAutoScroll(), 100);
        }
      }
    }, 10); // Small delay to allow DOM updates

    if (newComponent) {
      const newComponentReference = componentsReference.current[newComponent.id];
      if (newComponentReference) {
        newComponentReference.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      setNewComponent();
    }

    return () => clearTimeout(scrollTimeout);
  }, [activeComponentId, newComponent, pageIndex, expandedIdsByPage]);

  useLayoutEffect(() => {
    if (newComponent) {
      setExpandedIdsByPage((previous) => ({
        ...previous,
        [pageIndex]: [...(previous[pageIndex] || []), newComponent.parent],
      }));
    }
  }, [newComponent]);

  const handleClick = (event) => {
    if (!filterPanel.current?.contains(event.target)) {
      event.stopPropagation();
      setShowFilters(false);
    }
  };

  useEffect(() => {
    if (showFilters) {
      document.addEventListener('click', handleClick, true);
    } else {
      document.removeEventListener('click', handleClick, true);
    }
    return () => document.removeEventListener('click', handleClick, true);
  }, [showFilters]);

  const toggleExpansion = (id) => {
    if (expandedIdsByPage[pageIndex]?.includes(id)) {
      setExpandedIdsByPage((previous) => ({
        ...previous,
        [pageIndex]: previous[pageIndex].filter((index) => index !== id),
      }));
    } else {
      setExpandedIdsByPage((previous) => ({
        ...previous,
        [pageIndex]: [...(previous[pageIndex] || []), id],
      }));
    }
  };

  const mergeNodeWithItem = (node, shown) => {
    const item = shown?.find((s) => s.id === node.id);
    if (!item) return node;
    const merged = { ...item, ...node };
    if (Array.isArray(node.children)) {
      merged.children = node.children.map((child) => mergeNodeWithItem(child, shown));
    }
    return merged;
  };

  const renderTree = (node, depth = 1, parentDesignation = '') => {
    const { id, children = [] } = node;

    const end = endingDepth === 0 ? Infinity : endingDepth;
    let nodes = null;
    let hasVisibleChildren = false;

    if (!id) return null;
    if (children.length > 0) {
      const itemDesignationReference = depth >= startingDepth ? node.itemDesignation : '';
      nodes = children.map((child) => renderTree(child, depth + 1, itemDesignationReference));
      for (const child of children) {
        if (!hasVisibleChildren && shownIds.includes(child.id) && depth < end) {
          hasVisibleChildren = true;
        }
      }
    }

    const item = mergeNodeWithItem(node, shown);

    if (!flat) {
      if (parentDesignation && node.itemDesignation.indexOf(parentDesignation) === 0) {
        item.simpleDesignation = item.itemDesignation.replace(parentDesignation, '');
      }
      item.models = (item.models || []).map((m) => ({ ...m, ...modelsHashmap[m.modelId] }));
    }

    if (shownIds.includes(id) && depth >= startingDepth && depth <= end) {
      return (
        <TreeItem
          key={id}
          ref={(element, compId) => {
            componentsReference.current[compId] = element;
          }}
          depth={depth - startingDepth + 1}
          expanded={!!expandedIdsByPage[pageIndex]?.includes(id)}
          hasChildren={hasVisibleChildren}
          item={item}
          isSelected={id === activeComponentId}
          toggleExpansion={toggleExpansion}
          treeRoot={compsList.current}
        >
          {nodes}
        </TreeItem>
      );
    }
    return nodes;
  };

  return (
    <div
      className="components-panel--tree"
      role="tree"
    >
      {toolbar ? (
        <div className="tree-toolbar">
          <ComponentSelect
            isMulti={false}
            value={activeComponentId}
            onChange={onSearchSelect}
            closeMenuOnSelect
            placeholder="Search components..."
            availableComponents={shown || []}
            isClearable
            rootComponentId={rootComponent?.id}
            styles={{
              control: {
                minHeight: 'unset',
                lineHeight: 'unset',
              },
              clearIndicator: {
                padding: '6px',
              },
              dropdownIndicator: {
                padding: '6px',
              },
            }}
          />
          <Button
            onClick={reset}
            activity="secondary"
            icon={<Icon icon="he-load" />}
            tooltip="Reset component tree"
          />
          {canDelete && showDeleteTreeButton ? (
            <Button
              onClick={() => setShowDeleteDialog(true)}
              activity="danger"
            >
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <div
        data-testid="tree-root"
        className={`tree-root ${!flat && !toolbar ? 'full' : ''} custom-thin-scrollbar`}
        ref={compsList}
        onClick={() => dispatch(setActiveComponentId(activeComponentId))}
      >
        {renderTree(componentTree)}
      </div>
    </div>
  );
};

export default ComponentTree;
